// @tennis-matches/services/platform/settingsContributions
// Erweiterbare Einstellungen-Hub-Registry: Apps tragen Items in stabile Bereiche ein.

/** Kanonischer Hub-Pfad */
export const SETTINGS_HUB_PATH = "/hub/einstellungen";

/** Fester Hilfe-Link (unterhalb der Bereichsnavigation) */
export const SETTINGS_HUB_HELP_HREF = "/trainingsdesk/hilfe";

/**
 * Alte Kategorie-IDs → neue Bereichs-IDs (Bookmarks / SideNav / TD-Redirects).
 * @type {Readonly<Record<string, string>>}
 */
export const SETTINGS_HUB_LEGACY_CATEGORY_MAP = Object.freeze({
  training: "planning",
  finance: "billing",
  communication: "planning",
  general: "planning",
  integrations: "venues",
  access: "club",
  venues: "venues",
  // neue IDs bleiben stabil
  club: "club",
  planning: "planning",
  billing: "billing",
  apps: "apps",
});

/** @type {ReadonlyArray<{ id: string, titleKey: string, descriptionKey: string, iconKey: string, order: number }>} */
export const SETTINGS_HUB_CATEGORIES = Object.freeze([
  {
    id: "club",
    titleKey: "settings.hub.category.club.title",
    descriptionKey: "settings.hub.category.club.description",
    iconKey: "shield",
    order: 10,
  },
  {
    id: "planning",
    titleKey: "settings.hub.category.planning.title",
    descriptionKey: "settings.hub.category.planning.description",
    iconKey: "calendar",
    order: 20,
  },
  {
    id: "venues",
    titleKey: "settings.hub.category.venues.title",
    descriptionKey: "settings.hub.category.venues.description",
    iconKey: "mapPin",
    order: 30,
  },
  {
    id: "billing",
    titleKey: "settings.hub.category.billing.title",
    descriptionKey: "settings.hub.category.billing.description",
    iconKey: "wallet",
    order: 40,
  },
  {
    id: "apps",
    titleKey: "settings.hub.category.apps.title",
    descriptionKey: "settings.hub.category.apps.description",
    iconKey: "grid",
    order: 50,
  },
]);

/**
 * @typedef {'billing'|'analytics'|'clubApps'|'clubInsights'} SettingsRequiredFeature
 *
 * @typedef {{
 *   id: string,
 *   appKey: string,
 *   categoryId: string,
 *   titleKey: string,
 *   descriptionKey: string,
 *   href: string,
 *   iconKey?: string,
 *   order?: number,
 *   featureFlagKey?: string,
 *   actionKey?: string,
 *   descriptionKeyConnected?: string,
 *   scopeKey?: 'personal'|'tenant',
 *   requiredFeature?: SettingsRequiredFeature,
 *   connectionKind?: 'tennify'|'checkcourt'|'paypal'|'bank'|'whatsapp'|'outlook',
 * }} SettingsContribution
 */

/** @type {ReadonlyArray<SettingsContribution>} */
export const SETTINGS_CONTRIBUTIONS = Object.freeze([
  // Verein & Berechtigungen
  {
    id: "club-access",
    appKey: "clubhub",
    categoryId: "club",
    titleKey: "settings.hub.item.usersRoles.title",
    descriptionKey: "settings.hub.item.usersRoles.description",
    href: "/access-requests-admin",
    iconKey: "shield",
    order: 10,
    actionKey: "settings.hub.action.open",
    scopeKey: "tenant",
  },

  // Planung & Teilnahme
  {
    id: "td-planningFields",
    appKey: "trainerstunden",
    categoryId: "planning",
    titleKey: "trainerstunden.verwaltung.entry.planningFields.title",
    descriptionKey:
      "trainerstunden.verwaltung.entry.planningFields.description",
    href: "/trainingsdesk/stammdaten/trainingsanlage",
    iconKey: "clipboard",
    order: 10,
    actionKey: "settings.hub.action.customize",
    scopeKey: "tenant",
  },
  {
    id: "td-calendar",
    appKey: "trainerstunden",
    categoryId: "planning",
    titleKey: "trainerstunden.verwaltung.admin.item.calendar.title",
    descriptionKey: "trainerstunden.verwaltung.admin.item.calendar.description",
    href: "/trainingsdesk/stammdaten/einstellungen#ttb-settings-cal-hours",
    iconKey: "calendar",
    order: 20,
    actionKey: "settings.hub.action.change",
    scopeKey: "tenant",
  },
  {
    id: "td-holidays",
    appKey: "trainerstunden",
    categoryId: "planning",
    titleKey: "trainerstunden.verwaltung.admin.item.holidays.title",
    descriptionKey: "trainerstunden.verwaltung.admin.item.holidays.description",
    href: "/trainingsdesk/stammdaten/einstellungen#ttb-settings-state",
    iconKey: "calendar",
    order: 30,
    actionKey: "settings.hub.action.change",
    scopeKey: "tenant",
  },
  {
    id: "td-attendance",
    appKey: "trainerstunden",
    categoryId: "planning",
    titleKey: "trainerstunden.verwaltung.admin.item.attendance.title",
    descriptionKey:
      "trainerstunden.verwaltung.admin.item.attendance.description",
    href: "/trainingsdesk/stammdaten/einstellungen#ttb-settings-attendance-auto-confirm",
    iconKey: "clipboard",
    order: 40,
    actionKey: "settings.hub.action.change",
    scopeKey: "tenant",
  },
  {
    id: "td-reminders",
    appKey: "trainerstunden",
    categoryId: "planning",
    titleKey: "trainerstunden.verwaltung.admin.item.reminders.title",
    descriptionKey:
      "trainerstunden.verwaltung.admin.item.reminders.description",
    descriptionKeyConnected:
      "trainerstunden.verwaltung.admin.item.reminders.descriptionConnected",
    href: "/trainingsdesk/stammdaten/einstellungen#ttb-settings-rsvp-reminder",
    iconKey: "message",
    order: 50,
    actionKey: "settings.hub.action.setup",
    scopeKey: "tenant",
    connectionKind: "whatsapp",
  },
  {
    id: "td-lists",
    appKey: "trainerstunden",
    categoryId: "planning",
    titleKey: "trainerstunden.verwaltung.entry.lists.title",
    descriptionKey: "trainerstunden.verwaltung.entry.lists.description",
    href: "/trainingsdesk/stammdaten/auswahllisten",
    iconKey: "clipboard",
    order: 60,
    actionKey: "settings.hub.action.open",
    scopeKey: "tenant",
  },
  {
    id: "td-outlook",
    appKey: "trainerstunden",
    categoryId: "planning",
    titleKey: "settings.hub.item.outlook.title",
    descriptionKey: "settings.hub.item.outlook.description",
    descriptionKeyConnected: "settings.hub.item.outlook.descriptionConnected",
    href: "/trainingsdesk/stammdaten/einstellungen#ttb-settings-outlook",
    iconKey: "calendar",
    order: 70,
    actionKey: "settings.hub.action.setup",
    scopeKey: "personal",
    connectionKind: "outlook",
  },

  // Anlagen & Plätze
  {
    id: "club-locations",
    appKey: "clubhub",
    categoryId: "venues",
    titleKey: "clubhub.module.locations.title",
    descriptionKey: "clubhub.module.locations.subtitle",
    href: "/verwaltung/orte",
    iconKey: "mapPin",
    order: 10,
    actionKey: "settings.hub.action.open",
    scopeKey: "tenant",
  },
  {
    id: "belegung-admin",
    appKey: "belegung",
    categoryId: "venues",
    titleKey: "settings.hub.item.belegungCourts.title",
    descriptionKey: "settings.hub.item.belegungCourts.description",
    href: "/belegung/admin",
    iconKey: "mapPin",
    order: 20,
    actionKey: "settings.hub.action.open",
    scopeKey: "tenant",
    requiredFeature: "clubApps",
  },
  {
    id: "belegung-settings",
    appKey: "belegung",
    categoryId: "venues",
    titleKey: "settings.hub.item.belegungSettings.title",
    descriptionKey: "settings.hub.item.belegungSettings.description",
    href: "/belegung/settings",
    iconKey: "settings",
    order: 30,
    actionKey: "settings.hub.action.change",
    scopeKey: "tenant",
    requiredFeature: "clubApps",
  },
  {
    id: "td-tennify",
    appKey: "trainerstunden",
    categoryId: "venues",
    titleKey: "trainerstunden.verwaltung.admin.item.tennify.title",
    descriptionKey: "trainerstunden.verwaltung.admin.item.tennify.description",
    descriptionKeyConnected:
      "trainerstunden.verwaltung.admin.item.tennify.descriptionConnected",
    href: "/trainingsdesk/stammdaten/einstellungen#ttb-settings-tennify",
    iconKey: "link",
    order: 40,
    actionKey: "settings.hub.action.setup",
    scopeKey: "tenant",
    connectionKind: "tennify",
  },
  {
    id: "td-checkcourt",
    appKey: "trainerstunden",
    categoryId: "venues",
    titleKey: "trainerstunden.verwaltung.admin.item.checkcourt.title",
    descriptionKey:
      "trainerstunden.verwaltung.admin.item.checkcourt.description",
    descriptionKeyConnected:
      "trainerstunden.verwaltung.admin.item.checkcourt.descriptionConnected",
    href: "/trainingsdesk/stammdaten/einstellungen#ttb-settings-checkcourt",
    iconKey: "link",
    order: 45,
    actionKey: "settings.hub.action.setup",
    scopeKey: "tenant",
    connectionKind: "checkcourt",
  },

  // Rechnungen & Zahlungen
  {
    id: "td-invoiceLayout",
    appKey: "trainerstunden",
    categoryId: "billing",
    titleKey: "trainerstunden.verwaltung.entry.invoiceLayout.title",
    descriptionKey: "trainerstunden.verwaltung.entry.invoiceLayout.description",
    href: "/trainingsdesk/stammdaten/rechnungslayout",
    iconKey: "fileText",
    order: 10,
    actionKey: "settings.hub.action.customize",
    scopeKey: "tenant",
    requiredFeature: "billing",
  },
  {
    id: "td-billingMail",
    appKey: "trainerstunden",
    categoryId: "billing",
    titleKey: "trainerstunden.verwaltung.admin.item.billingMail.title",
    descriptionKey:
      "trainerstunden.verwaltung.admin.item.billingMail.description",
    href: "/trainingsdesk/stammdaten/einstellungen#ttb-settings-billing-mail",
    iconKey: "fileText",
    order: 20,
    actionKey: "settings.hub.action.change",
    scopeKey: "tenant",
    requiredFeature: "billing",
  },
  {
    id: "td-bank",
    appKey: "trainerstunden",
    categoryId: "billing",
    titleKey: "trainerstunden.verwaltung.admin.item.bank.title",
    descriptionKey: "trainerstunden.verwaltung.admin.item.bank.description",
    descriptionKeyConnected:
      "trainerstunden.verwaltung.admin.item.bank.descriptionConnected",
    href: "/trainingsdesk/stammdaten/einstellungen#ttb-settings-bank",
    iconKey: "wallet",
    order: 30,
    actionKey: "settings.hub.action.setup",
    scopeKey: "tenant",
    requiredFeature: "billing",
    connectionKind: "bank",
  },
  {
    id: "td-paypal",
    appKey: "trainerstunden",
    categoryId: "billing",
    titleKey: "trainerstunden.verwaltung.admin.item.paypal.title",
    descriptionKey: "trainerstunden.verwaltung.admin.item.paypal.description",
    descriptionKeyConnected:
      "trainerstunden.verwaltung.admin.item.paypal.descriptionConnected",
    href: "/trainingsdesk/stammdaten/einstellungen#ttb-settings-paypal",
    iconKey: "wallet",
    order: 40,
    actionKey: "settings.hub.action.setup",
    scopeKey: "tenant",
    requiredFeature: "billing",
    connectionKind: "paypal",
  },
  {
    // Operativer Export: Einstieg bleibt, bis Finanzbereich den Export übernimmt.
    id: "td-dataExport",
    appKey: "trainerstunden",
    categoryId: "billing",
    titleKey: "trainerstunden.verwaltung.entry.dataExport.title",
    descriptionKey: "trainerstunden.verwaltung.entry.dataExport.description",
    href: "/trainingsdesk/stammdaten/datenexport",
    iconKey: "download",
    order: 50,
    actionKey: "settings.hub.action.open",
    scopeKey: "tenant",
    requiredFeature: "billing",
  },

  // Apps & Abo
  {
    id: "td-vereinsabo",
    appKey: "trainerstunden",
    categoryId: "apps",
    titleKey: "trainerstunden.verwaltung.admin.category.subscription.title",
    descriptionKey:
      "trainerstunden.verwaltung.admin.category.subscription.description",
    href: "/trainingsdesk/stammdaten/vereinsabo",
    iconKey: "grid",
    order: 10,
    actionKey: "settings.hub.action.open",
    scopeKey: "tenant",
  },
]);

/**
 * @param {string} categoryId
 * @returns {string}
 */
export function normalizeSettingsHubCategoryId(categoryId) {
  const raw = String(categoryId || "").trim();
  if (!raw) return "";
  return SETTINGS_HUB_LEGACY_CATEGORY_MAP[raw] || raw;
}

/**
 * @param {{
 *   enabledAppKeys?: Iterable<string>|null,
 *   isAppEnabled?: (appKey: string) => boolean,
 *   subscriptionFeatures?: {
 *     billing?: boolean,
 *     analytics?: boolean,
 *     clubApps?: boolean,
 *     clubInsights?: boolean,
 *   }|null,
 *   bypassSubscriptionFeatures?: boolean,
 * }} [opts]
 * @returns {{
 *   categories: Array<{
 *     id: string,
 *     titleKey: string,
 *     descriptionKey: string,
 *     iconKey: string,
 *     order: number,
 *     items: SettingsContribution[],
 *   }>,
 *   items: SettingsContribution[],
 *   helpHref: string,
 * }}
 */
export function getSettingsHubModel(opts = {}) {
  const enabledSet =
    opts.enabledAppKeys != null
      ? new Set(
          [...opts.enabledAppKeys]
            .map((k) => String(k || "").trim())
            .filter(Boolean),
        )
      : null;
  const isAppEnabled =
    typeof opts.isAppEnabled === "function"
      ? opts.isAppEnabled
      : (appKey) => {
          if (!enabledSet) return true;
          if (!enabledSet.size) return true;
          return enabledSet.has(String(appKey || "").trim());
        };

  const bypassFeatures = Boolean(opts.bypassSubscriptionFeatures);
  const features =
    opts.subscriptionFeatures && typeof opts.subscriptionFeatures === "object"
      ? opts.subscriptionFeatures
      : null;
  const featureAllowed = (requiredFeature) => {
    if (!requiredFeature || bypassFeatures) return true;
    if (!features) return true;
    return Boolean(features[requiredFeature]);
  };

  const items = SETTINGS_CONTRIBUTIONS.filter(
    (c) => isAppEnabled(c.appKey) && featureAllowed(c.requiredFeature),
  ).slice();
  items.sort(
    (a, b) =>
      (Number(a.order) || 0) - (Number(b.order) || 0) ||
      String(a.id).localeCompare(String(b.id)),
  );

  const byCat = new Map();
  for (const item of items) {
    const list = byCat.get(item.categoryId) || [];
    list.push(item);
    byCat.set(item.categoryId, list);
  }

  const categories = SETTINGS_HUB_CATEGORIES.map((cat) => ({
    ...cat,
    items: byCat.get(cat.id) || [],
  }))
    .filter((cat) => cat.items.length > 0)
    .sort((a, b) => a.order - b.order);

  return { categories, items, helpHref: SETTINGS_HUB_HELP_HREF };
}

/**
 * @param {string} categoryId
 * @returns {string}
 */
export function buildSettingsHubCategoryPath(categoryId) {
  const id = normalizeSettingsHubCategoryId(categoryId);
  if (!id) return SETTINGS_HUB_PATH;
  return `${SETTINGS_HUB_PATH}?cat=${encodeURIComponent(id)}`;
}

/**
 * @param {string} [search]
 * @returns {string}
 */
export function parseSettingsHubCategoryFromSearch(search) {
  try {
    const q = new URLSearchParams(
      String(search || "").replace(/^\?/, "") || "",
    );
    return normalizeSettingsHubCategoryId(q.get("cat"));
  } catch {
    return "";
  }
}
